import { z } from "zod/v4"
import { ShopCustomTagId, ItemIdSchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { Period, PeriodState } from "./common"
import { DocumentStatus } from "../common"

export const ShopCustomTagKind = z.enum(["custom", "tag"]).describe("ルール設定の種類")
export type ShopCustomTagKind = z.infer<typeof ShopCustomTagKind>

export const ShopCustomTagColor = z.enum(["none", "magenta", "aqua"]).describe("グリッド表示の背景カラー設定")
export type ShopCustomTagColor = z.infer<typeof ShopCustomTagColor>

export const ShopCustomTagOrder = z.object({
  column: z.string().describe("並び替えのカラム"),
  direction: z.enum(["asc", "desc"]).describe("並び替えの方向"),
})
export type ShopCustomTagOrder = z.infer<typeof ShopCustomTagOrder>

export const ShopCustomTagTag = z.object({
  value: z.string().min(1).max(20).describe("タグ文字列"),
  order: ShopCustomTagOrder.describe("並び替え設定"),
})
export type ShopCustomTagTag = z.infer<typeof ShopCustomTagTag>

export const ShopCustomTagDocumentMeta = z.object({
  kind: ShopCustomTagKind.describe("ルール設定の種類"),
  title: z.string().min(1).max(50).describe("カスタムタグのタイトル"),
  managed_image_id: z.string().nullable().describe("カスタムタグTOP画像のID（オプション）"),
  description: z.string().min(1).max(50).nullable().describe("画像上に表示するテキスト（オプション）"),
  link: z.string().nullable().describe("画像タップ時の遷移URI（オプション）"),
  priority: z.int().min(1).max(99_999).describe("優先度（1-99999）"),
  color: ShopCustomTagColor.describe("グリッド表示の背景カラー設定"),
  show_on_top: z.boolean().describe("ショップTOPに表示するかどうか"),
  items: z.array(z.string()).nullable().describe("手動指定時のアイテムID一覧（kind='custom'の場合のみ）"),
  tag: ShopCustomTagTag.nullable().describe("タグ検索時のタグ情報（kind='tag'の場合のみ）"),
  display_period: Period.describe("表示期間"),
  hide_expired_items: z.boolean().describe("販売終了商品を非表示にするかどうか"),
  status: DocumentStatus.describe("タグの状態"),
  planning_management_id: z.string().nullable().describe("企画管理用の Uniq ID（管理番号）。最大12文字。AES-CBC で暗号化された文字列が格納される。").optional(),
  planning_name: z.string().nullable().describe("企画管理用名称。最大50文字。AES-CBC で暗号化された文字列が格納される。").optional(),
})

export type ShopCustomTagDocumentMeta = z.infer<typeof ShopCustomTagDocumentMeta>

export const ShopCustomTagDocumentBody = z.intersection(
  ShopCustomTagDocumentMeta,
  z.object({
    reference_managed_image_ids: z.array(z.string()).describe("使用している画像のIDリスト（自動計算）"),
    display_period_state: PeriodState.describe("表示期間の状態（システム自動計算）"),
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type ShopCustomTagDocumentBody = z.infer<typeof ShopCustomTagDocumentBody>

export type ShopCustomTagDocument = ShopCustomTagDocumentBody & {
  id: ShopCustomTagId,
}

export class ShopCustomTagDocuments {

  static toDocumentData(tag: ShopCustomTagDocument): object {
    return {
      ...tag,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): ShopCustomTagDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: ShopCustomTagId(doc.id),
      kind: data["kind"],
      title: data["title"],
      managed_image_id: data["managed_image_id"],
      description: data["description"],
      link: data["link"],
      priority: data["priority"],
      color: data["color"],
      show_on_top: data["show_on_top"],
      items: data["items"],
      tag: data["tag"],
      display_period: data["display_period"],
      hide_expired_items: data["hide_expired_items"] ?? false,
      display_period_state: data["display_period_state"],
      reference_managed_image_ids: data["reference_managed_image_ids"] || [],
      planning_management_id: data["planning_management_id"] ?? null,
      planning_name: data["planning_name"] ?? null,
      status: data["status"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

/* eslint-disable popopo-msg/firestore-snake-case */
const CreateShopCustomTagCommonFields = {
  title: z.string().min(1).max(50).describe("カスタムタグのタイトル"),
  managedImageId: z.string().nullable().optional().describe("カスタムタグTOP画像のID（オプション）"),
  description: z.string().min(1).max(50).optional().describe("画像上に表示するテキスト（オプション）"),
  link: z.string().min(1).optional().describe("画像タップ時の遷移URI（オプション）"),
  priority: z.int().min(1).max(99_999).default(1).describe("優先度（1-99999）"),
  color: ShopCustomTagColor.describe("グリッド表示の背景カラー設定"),
  showOnTop: z.boolean().describe("ショップTOPに表示するかどうか"),
  displayPeriod: z.object({
    startAt: z.number().describe("表示開始日時 (UNIX Timestamp mills)"),
    endAt: z.number().nullable().describe("表示終了日時 (UNIX Timestamp mills、nullの場合は無期限)"),
  }).describe("表示期間"),
  hideExpiredItems: z.boolean().default(false).describe("販売終了商品を非表示にするかどうか"),
  status: DocumentStatus.describe("タグの状態"),
  planningManagementId: z.string().nullable().optional().describe("企画管理用の Uniq ID（管理番号）。AES-CBC で暗号化された文字列。平文は最大12文字。"),
  planningName: z.string().nullable().optional().describe("企画管理用名称。AES-CBC で暗号化された文字列。平文は最大50文字。"),
}
/* eslint-enable popopo-msg/firestore-snake-case */

export const CreateShopCustomTagCommonRequest = z.object(CreateShopCustomTagCommonFields)

export const CreateShopCustomTagRequest = z.discriminatedUnion("kind", [
  z.strictObject({
    ...CreateShopCustomTagCommonFields,
    kind: z.literal("custom"),
    items: z.array(ItemIdSchema).min(1).max(1000).describe("手動指定時のアイテムID一覧"),
  }),
  z.strictObject({
    ...CreateShopCustomTagCommonFields,
    kind: z.literal("tag"),
    tag: z.object({
      value: z.string().min(1).max(20).describe("タグ文字列"),
      order: z.object({
        column: z.string().describe("並び替えのカラム"),
        direction: z.enum(["asc", "desc"]).describe("並び替えの方向"),
      }).describe("並び替え設定"),
    }).describe("タグ検索時のタグ情報"),
  }),
])

export type CreateShopCustomTagRequest = z.infer<typeof CreateShopCustomTagRequest>

export const UpdateShopCustomTagRequest = z.object({
  title: z.string().min(1).max(50).optional().describe("カスタムタグのタイトル"),
  managedImageId: z.string().nullable().optional().describe("カスタムタグTOP画像のID（オプション、nullで削除）"),
  description: z.string().min(1).max(50).optional().describe("画像上に表示するテキスト（オプション）"),
  link: z.string().min(1).optional().describe("画像タップ時の遷移URI（オプション）"),
  priority: z.int().min(1).max(99_999).optional().describe("優先度（1-99999）"),
  color: ShopCustomTagColor.optional().describe("グリッド表示の背景カラー設定"),
  showOnTop: z.boolean().optional().describe("ショップTOPに表示するかどうか"),
  items: z.array(ItemIdSchema).min(1).max(1000).optional().describe("手動指定時のアイテムID一覧（kind='custom'の場合のみ）"),
  tag: z.object({
    value: z.string().min(1).max(20).describe("タグ文字列"),
    order: z.object({
      column: z.string().describe("並び替えのカラム"),
      direction: z.enum(["asc", "desc"]).describe("並び替えの方向"),
    }).describe("並び替え設定"),
  }).optional().describe("タグ検索時のタグ情報（kind='tag'の場合のみ）"),
  displayPeriod: z.object({
    startAt: z.number().describe("表示開始日時 (UNIX Timestamp mills)"),
    endAt: z.number().nullable().describe("表示終了日時 (UNIX Timestamp mills、nullの場合は無期限)"),
  }).optional().describe("表示期間"),
  hideExpiredItems: z.boolean().optional().describe("販売終了商品を非表示にするかどうか"),
  status: DocumentStatus.optional().describe("タグの状態"),
  planningManagementId: z.string().nullable().optional().describe("企画管理用の Uniq ID（管理番号）。AES-CBC で暗号化された文字列。平文は最大12文字。"),
  planningName: z.string().nullable().optional().describe("企画管理用名称。AES-CBC で暗号化された文字列。平文は最大50文字。"),
}).refine(
  (data) => !(data.items !== undefined && data.tag !== undefined),
  {
      error: "items と tag は同時に指定できません"
}
)

export type UpdateShopCustomTagRequest = z.infer<typeof UpdateShopCustomTagRequest>
